import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './UserDetail.module.css';
import axios from 'axios';
import { API_BASE_URL } from '../../../apiConfig';

export default function UserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [userInfo, setUserInfo] = useState(null);
  const [carInfo, setCarInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 테이블 데이터 상태
  const [filterHistory, setFilterHistory] = useState([]);
  const [resistanceLog, setResistanceLog] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [resistancePage, setResistancePage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [resistanceTotalPages, setResistanceTotalPages] = useState(1);

  // 회원 정보 불러오기
  useEffect(() => {
    const loadUserDetail = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/api/v1/admin/users/${userId}`);
        console.log("서버 응답:", response.data);
        setUserInfo(response.data.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.message || '회원 정보 불러오는 데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };
    loadUserDetail();
  }, [userId]);

  // 차량 정보 불러오기
  useEffect(() => {
    const loadCarInfo = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/cars/${userId}`);
        console.log("차량 정보 응답:", response.data);
        setCarInfo(response.data.data);
      } catch (err) {
        console.error('차량 정보 불러오는 데 실패했습니다.', err);
      }
    };
    loadCarInfo();
  }, [userId]);



  // 필터 교체 이력 불러오기
  useEffect(() => {
    const loadFilterHistory = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/v1/admin/users/${userId}/filter-history?page=${historyPage}&size=5`
        );

        const historyData = response.data.data;

        setFilterHistory(historyData.items);
        const hasNext = historyData.pagination.hasNext;
        setHistoryTotalPages(hasNext ? historyPage + 1 : historyPage);
      } catch (err) {
        console.error('필터 교체 이력 로딩 실패:', err);
      }
    };

    loadFilterHistory();
  }, [userId, historyPage]);

  // 저항값 측정 이력 불러오기
  useEffect(() => {
    const loadResistanceLog = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/filter-history/resistance/user/${userId}`);
        console.log('저항값 측정 이력 응답:', response.data);
        
        if (response.data.code === 1) {
          const data = response.data.data;
          if (data && data.length > 0) {
            // 데이터를 변환하여 순번을 아래부터 1로 시작하도록 설정
            const transformedData = data.map((item, index) => ({
              resistance_log_id: data.length - index, // 아래부터 1로 시작
              resistance_value: item.resistanceValue,
              measured_at: item.measuredAt
            }));
            setResistanceLog(transformedData);
          } else {
            setResistanceLog([]);
          }
        } else {
          console.error('저항값 측정 이력 로딩 실패');
          setResistanceLog([]);
        }
      } catch (err) {
        console.error('저항값 측정 이력 로딩 실패:', err);
        setResistanceLog([]);
      }
    };

    loadResistanceLog();
  }, [userId, resistancePage]);

  // 페이지 변경 핸들러
  const handleHistoryPageChange = (page) => {
    console.log('클릭된 페이지:', page);
    setHistoryPage(page);
  };

  const handleResistancePageChange = (page) => {
    setResistancePage(page);
  };

  // 뒤로가기
  const handleBack = () => {
    navigate('/users');
  };

  // 페이지네이션 렌더링 함수
  const renderPagination = (currentPage, totalPages, onPageChange, className) => {
    const pages = [];
    const maxPagesToShow = 5;
    const effectiveTotalPages = totalPages;

    // 현재 페이지 그룹의 시작 페이지를 계산
    const startPage = Math.floor((currentPage - 1) / maxPagesToShow) * maxPagesToShow + 1;
    // 현재 페이지 그룹의 끝 페이지를 계산 (전체 페이지 수를 넘지 않도록)
    const endPage = Math.min(startPage + maxPagesToShow - 1, effectiveTotalPages);
    
    // 이전 페이지 그룹으로 이동하는 화살표 버튼
    if (startPage > 1) {
      pages.push(
        <button key="prev" className={styles.paginationArrow} onClick={() => onPageChange(startPage - 1)}>
          &lt;
        </button>
      );
    }

    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          className={`${styles.paginationButton} ${currentPage === i ? styles.active : ''}`}
          onClick={() => onPageChange(i)}
        >
          {i}
        </button>
      );
    }
    // 다음 페이지 그룹으로 이동하는 화살표 버튼
    if (endPage < totalPages) {
      pages.push(
        <button key="next" className={styles.paginationArrow} onClick={() => onPageChange(endPage + 1)}>
          &gt;
        </button>
      );
    }

    return <div className={className}>{pages}</div>;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          회원 정보를 불러오는 중...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <h3>오류가 발생했습니다</h3>
          <p>{error}</p>
          <button onClick={handleBack} style={{ padding: '10px 20px', marginTop: '10px' }}>
            목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* 왼쪽 섹션 */}
      <div className={styles.rowLayout}>
        <div className={styles.leftColumn}>
          {/* 회원 기본 정보 카드 */}
          <div className={styles.userInfoSection}>
            <h3 className={styles.cardTitle}>회원 기본 정보</h3>
            <div className={styles.infoTable}>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>이름</span>
                <span className={styles.infoValue}>{userInfo.name}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>이메일</span>
                <span className={styles.infoValue}>{userInfo.email}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>핸드폰 번호</span>
                <span className={styles.infoValue}>{userInfo.phone}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>생성일</span>
                <span className={styles.infoValue}>{userInfo.createdAt.split('T')[0]}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>푸시 알림</span>
                <span className={styles.infoValue}>
                  {userInfo.push_enabled ? 'Y' : 'N'}
                </span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>상태</span>
                <span className={styles.infoValue}>
                  {userInfo.status === 'ACTIVE' ? 'Y' : 'N'}
                </span>
              </div>
              <div style={{ height: '20px' }} />
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>차량 번호</span>
                <span className={styles.infoValue}>{carInfo?.carNumber}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>차 모델</span>
                <span className={styles.infoValue}>{carInfo?.model}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>차 세부 모델</span>
                <span className={styles.infoValue}>{carInfo?.modelDetail}</span>
              </div>
            </div>
          </div>

          {/* 필터 교체 이력 카드 */}
          <div className={styles.userInfoSection}>
            <h3 className={styles.cardTitle}>필터 교체 이력</h3>
            <div className={styles.historyTable}>
              <div className={styles.historyHeader}>
                <div className={styles.historyCol}>필터 ID</div>
                <div className={styles.historyCol}>교체일시</div>
              </div>
              {filterHistory.map((item, index) => (
                <div key={index} className={styles.historyRow}>
                  <div className={styles.historyCol}>{item.filterChangeHistoryId}</div>
                  <div className={styles.historyCol}>
                    {new Date(item.replacementDate).toLocaleDateString('ko-KR')}
                  </div>
                </div>
              ))}
            </div>
            {historyTotalPages >= 1 && 
              renderPagination(historyPage, historyTotalPages, handleHistoryPageChange, styles.pagination)
            }
          </div>
        </div>

        {/* 오른쪽 섹션 */}
        <div className={styles.rightColumn}>
          <div className={styles.userInfoSection}>
            <h3 className={styles.cardTitle}>저항값 측정 이력</h3>
            <div className={styles.resistanceTable}>
              <div className={styles.resistanceHeader}>
                <div className={styles.resistanceCol}>순차</div>
                <div className={styles.resistanceCol}>저항값</div>
                <div className={styles.resistanceCol}>측정일시</div>
              </div>
              {resistanceLog.map((item, index) => (
                <div key={index} className={styles.resistanceRow}>
                  <div className={styles.resistanceCol}>{item.resistance_log_id}</div>
                  <div className={styles.resistanceCol}>{item.resistance_value}</div>
                  <div className={styles.resistanceCol}>
                    {item.measured_at.split('T')[0]}
                  </div>
                </div>
              ))}
            </div>
            {resistanceTotalPages > 1 && 
              renderPagination(resistancePage, resistanceTotalPages, handleResistancePageChange, styles.fullPagination)
            }
          </div>
        </div>
      </div>
    </div>
  );
}